import { BaseService } from '../../../http/BaseHttpService';
import { IProGPTChatSessionsService } from '../interfaces/IProGPTChatSessionsService';
import { ProGPTProblemDetailsDto } from '../dtos/responses/ProGPTProblemDetailsDto';
import { ProGPTResultDto } from '../dtos/responses/ProGPTResultDto';
import { ProGPTChatSessionDto } from '../dtos/responses/ProGPTChatSessionDto';
import { DownloadChatSession } from '../dtos/responses/ProGPTDownloadChatSessionDto';
import {
    ChatSessionLoadResult,
    ChatSessionsLoadResult,
    DeleteChatSessionsRequest,
} from '../../../chat/interfaces/IChatSessionsService';
import { mapProGPTChatSessionDtoToModel } from '../mappers/ProGPTChatSessionMapper';
import { buildProGPTUrl } from '../../../../features/utils/buildProGPTUrl';
import { ChatProviderType } from '../../../../domain/chat/models/ChatProviderType';

export class ProGPTChatSessionsService extends BaseService implements IProGPTChatSessionsService {
    public readonly providerType = ChatProviderType.ProGPT;

    constructor(private readonly baseUrl: string) {
        super();
    }

    public async getAllChatsAsync(accessToken: string): Promise<ChatSessionsLoadResult> {
        const response = await this.getResponseAsync<ProGPTResultDto<ProGPTChatSessionDto[]>>(
            {
                url: buildProGPTUrl(this.baseUrl, 'ChatSession/getAllChats'),
                method: 'GET',
            },
            accessToken,
        );

        if (!response.isSuccess) {
            throw new Error(response.errors?.join(', ') || 'Failed to load ProGPT chat sessions.');
        }
        return {
            sessions: response.value.map(mapProGPTChatSessionDtoToModel),
            documentsBySessionId: {},
        };
    }

    public async getChatAsync(chatId: string, accessToken: string): Promise<ChatSessionLoadResult> {
        const response = await this.getResponseAsync<ProGPTResultDto<ProGPTChatSessionDto>>(
            {
                url: buildProGPTUrl(this.baseUrl, `ChatSession/${chatId}`),
                method: 'GET',
            },
            accessToken,
        );

        if (!response.isSuccess) {
            throw new Error(response.errors?.join(', ') || 'Failed to load ProGPT chat session.');
        }

        return {
            session: mapProGPTChatSessionDtoToModel(response.value),
            documents: [],
        };
    }

    public async downloadChatSessionAsync(chatId: string, accessToken: string): Promise<DownloadChatSession> {
        const response = await this.getResponseAsync<ProGPTResultDto<DownloadChatSession>>(
            {
                url: buildProGPTUrl(this.baseUrl, `ChatSession/${chatId}/download`),
                method: 'GET',
            },
            accessToken,
        );

        if (!response.isSuccess) {
            throw new Error(response.errors?.join(', ') || 'Failed to download ProGPT chat session.');
        }

        return response.value;
    }

    public async deleteChatAsync(request: DeleteChatSessionsRequest, accessToken: string): Promise<void> {
        if (!request.chatId) {
            return;
        }
        try {
            const response = await this.getResponseAsync<ProGPTResultDto<Record<string, never>>>(
                {
                    url: buildProGPTUrl(this.baseUrl, `ChatSession/${request.chatId}`),
                    method: 'DELETE',
                },
                accessToken,
            );

            if (!response.isSuccess) {
                throw new Error(response.errors?.join(', ') || 'Failed to delete ProGPT chat session.');
            }
        } catch (error) {
            this.handleDeleteError(error);
        }
    }

    private handleDeleteError(error: unknown): never {
        const errorMessage = error instanceof Error ? error.message : '';

        if (errorMessage.includes('400:')) {
            const detailsText = errorMessage.split('=>')[1]?.trim();

            if (detailsText) {
                try {
                    const problemDetails = JSON.parse(detailsText) as ProGPTProblemDetailsDto;
                    const flattenedErrors = Object.values(problemDetails.errors ?? {}).flat();
                    throw new Error(
                        flattenedErrors.join(', ') || problemDetails.title || 'Failed to delete ProGPT chat session.',
                    );
                } catch {
                    throw new Error(detailsText);
                }
            }
        }

        throw error;
    }
}
